import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';

import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { isTosAccepted, highlightTos } from 'utils/tosGate';
import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { useKitView } from 'containers/KitViewContext';
import { getPdfUrl, completeWorkspace } from 'containers/KitWorkspaceStorage/api';
import type { ISaveAssemblyPayload } from 'containers/KitWorkspaceStorage/types';

import Button from 'common/components/Button';
import BadgeButton from 'common/components/BadgeButton';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import MyAssembliesPopup from 'common/components/MyAssembliesPopup';
import CombinedPdfPreview from './CombinedPdfPreview';
import { useKitConfirmActions } from './useKitConfirmActions';
import { useKitCartCommit } from './useKitCartCommit';
import { useKitStartOver } from './useKitStartOver';
import { readBrandAccent, resolveLogoUrl } from './pdfBranding';
import styles from './KitConfirmActions.module.scss';

const NAME_MAX = 60;
const NOTES_MAX = 240;
// Delay highlighting the terms checkbox so it is scrolled into view first.
const TOS_HIGHLIGHT_DELAY_MS = 120;

interface IKitConfirmActionsProps {
  sessionId?: string;
  buildPayload: () => ISaveAssemblyPayload;
  onResetHandler: () => void;
  lockNavigation?: () => void;
  // wizard form id, for any form-tied buttons.
  idForm?: string;
  className?: string;
}

/**
 * Shared last-page action block for the kit journey. Per-project SummaryViews
 * render this inside their kitsEnabled branch; everything tenant-specific is
 * passed via buildPayload. Owns the assembly name/notes fields (injected into
 * the payload) and the action buttons. "Finish" generates the whole-workspace
 * PDF directly (no popup required) and moves to the final screen; "Preview" opens
 * the shared read-only combined preview (where the PDF can also be generated).
 * Once the workspace is `completed` the buttons swap to the final set (My
 * assemblies / Email / View PDF / Add to cart).
 */
const KitConfirmActions = ({
  sessionId, buildPayload, onResetHandler, lockNavigation, idForm, className,
}: IKitConfirmActionsProps) => {
  const t = useKitTranslate();
  const {
    workspaceKey, workspace, standalone, kits, totals, refetch,
    getPendingAssembly, setConfirmPageMounted,
  } = useKitWorkspace();
  const [showPopup, setShowPopup] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isOpeningAssemblies, setIsOpeningAssemblies] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');

  const completed = workspace?.status === 'completed';
  // Busy from the Finish click until the workspace settles (completing -> the
  // completed branch renders). Keeps the whole draft row disabled meanwhile.
  const generating = isFinishing || workspace?.status === 'completing';

  // While the confirm page is up, wizard edits (quantity/notes fields) refine
  // the committed assembly in place; SessionRotationBridge must not rotate the
  // session for them. Off this page, the next edit rotates.
  useEffect(() => {
    setConfirmPageMounted(true);
    return () => setConfirmPageMounted(false);
  }, [setConfirmPageMounted]);

  // A remount (round trip via the step tabs) resets the name/notes fields, but
  // the assembly may already be committed with them filled. Restore them once
  // from the saved row so the next patch does not wipe them.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current || !sessionId) return;
    const pending = getPendingAssembly(sessionId);
    if (!pending) {
      // Nothing committed for this session at mount time: never prefill, or a
      // field the user cleared after committing would come back.
      prefilledRef.current = true;
      return;
    }
    const saved = [...standalone, ...kits.flatMap((k) => k.lines.map((l) => l.assembly))]
      .find((a) => a.uuid === pending.assemblyId);
    if (!saved) return; // workspace not loaded yet, retry on the next update
    prefilledRef.current = true;
    if (!name && saved.name) setName(saved.name);
    if (!notes && saved.notes) setNotes(saved.notes);
  }, [sessionId, getPendingAssembly, standalone, kits, name, notes]);

  // Inject the user-entered name/notes into the tenant payload. An empty name
  // saves the assembly as "Untitled".
  const buildPayloadWithMeta = useCallback((): ISaveAssemblyPayload => ({
    ...buildPayload(),
    name: name.trim() || undefined,
    notes: notes.trim() || undefined,
  }), [buildPayload, name, notes]);

  const {
    isSaving,
    commitPendingAssembly,
    handleSaveAndCopy,
    handleSaveAndBuildAnother,
  } = useKitConfirmActions({
    sessionId, buildPayload: buildPayloadWithMeta, onResetHandler,
  });

  // Let the Manage Kits / preview "Finish" pull this still-pending assembly into
  // the combined PDF (as a Single Assembly) while the confirm page is mounted.
  const { registerPendingCommit } = useKitView();
  useEffect(() => {
    registerPendingCommit(commitPendingAssembly);
    return () => registerPendingCommit(null);
  }, [registerPendingCommit, commitPendingAssembly]);

  // After the whole-workspace PDF is generated, lock wizard navigation and grab
  // a fresh presigned URL for the Email / View PDF buttons. Driven by the
  // persisted workspace status, so it survives reopening Manage Kits.
  useEffect(() => {
    if (completed && lockNavigation) lockNavigation();
  }, [completed, lockNavigation]);

  useEffect(() => {
    let cancelled = false;
    if (completed && !pdfUrl && workspaceKey) {
      getPdfUrl(workspaceKey)
        .then(({ url }) => { if (!cancelled) setPdfUrl(url); })
        .catch(() => { /* View PDF stays unavailable; user can retry from preview */ });
    }
    return () => { cancelled = true; };
  }, [completed, pdfUrl, workspaceKey]);

  // A failed render releases the Finish spinner so the user can retry.
  useEffect(() => {
    if (workspace?.status === 'failed' && isFinishing) {
      setIsFinishing(false);
      showToast(ToastTypes.error, t('texts.kits.toast.pdfError'));
    }
  }, [workspace?.status, isFinishing, t]);

  const {
    cartEnabled, cartLabel, onAddToCart, isSendingCart,
  } = useKitCartCommit();
  const { requestStartOver, dialog: startOverDialog, isResetting } = useKitStartOver();

  const savedCount = standalone.length + kits.reduce((sum, k) => sum + k.lines.length, 0);
  const busy = isSaving;

  const openPdf = () => { if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer'); };
  const emailPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = `mailto:${t('variables.contactEmail')}?subject=${encodeURIComponent(t('variables.contactEmailTitle'))}&body=${encodeURIComponent(pdfUrl)}`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  };

  // "My assemblies": commit the still-pending assembly first (so the last build
  // lands in the list), then open the popup. No-op commit once completed/locked.
  const openMyAssemblies = useCallback(async () => {
    if (!completed) {
      setIsOpeningAssemblies(true);
      try {
        await commitPendingAssembly();
      } catch {
        // Open the popup anyway with whatever is already saved.
      } finally {
        setIsOpeningAssemblies(false);
      }
    }
    setShowPopup(true);
  }, [completed, commitPendingAssembly]);

  // "Finish": commit the pending assembly, then generate the whole-workspace PDF
  // straight away (no preview popup needed). The workspace flips to `completing`
  // and, once `completed`, this component renders its final screen. Preview stays
  // an optional read-only look before finishing.
  const handleFinish = useCallback(async () => {
    if (!isTosAccepted()) {
      showToast(ToastTypes.warning, t('texts.kits.toast.acceptTerms'));
      window.setTimeout(highlightTos, TOS_HIGHLIGHT_DELAY_MS);
      return;
    }
    setIsFinishing(true);
    try {
      const key = await commitPendingAssembly();
      if (!key) { setIsFinishing(false); return; }
      await completeWorkspace(key, readBrandAccent(), resolveLogoUrl(t('variables.pdf.logo')));
      track('pdf_generated', { workspaceKey: key, kits: totals.kits, configs: totals.configs });
      // Pick up status='completing' so the workspace query starts polling.
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.pdfError'));
      setIsFinishing(false);
    }
  }, [commitPendingAssembly, totals, refetch, t]);

  // Final screen after the whole-workspace PDF: keep "My assemblies" available
  // and surface Email / View PDF, with the Add to cart action last.
  if (completed) {
    return (
      <>
        <div className={classNames(styles.actions, className)}>
          <Button
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            className={styles.btn}
            onClick={requestStartOver}
            disabled={isResetting}
          >
            {`↺ ${t('texts.kits.startOver')}`}
          </Button>
          <BadgeButton
            label={t('texts.kits.myAssemblies')}
            count={savedCount}
            onClick={openMyAssemblies}
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            className={styles.btn}
          />
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            className={styles.btn}
            onClick={emailPdf}
            disabled={!pdfUrl}
            form={idForm}
          >
            {t('texts.kits.final.email')}
          </Button>
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            className={styles.btn}
            onClick={openPdf}
            disabled={!pdfUrl}
            form={idForm}
          >
            {t('texts.kits.final.viewPdf')}
          </Button>
          {cartEnabled && (
            <Button
              schema={ButtonSchema.primary}
              size={ButtonSize.small}
              className={classNames(styles.btn, styles.primary)}
              onClick={onAddToCart}
              disabled={isSendingCart}
              form={idForm}
            >
              {cartLabel}
            </Button>
          )}
        </div>

        {showPopup && <MyAssembliesPopup onClose={() => setShowPopup(false)} />}
        {/* Keep the preview mounted across the draft->completed flip: the user
            generates inside it and it then shows Download / Email / Add to cart. */}
        {showPreview && <CombinedPdfPreview onClose={() => setShowPreview(false)} />}
        {startOverDialog}
      </>
    );
  }

  return (
    <>
      <div className={styles.root}>
        <div className={styles.fields}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="kit-confirm-name">
              {t('texts.kits.confirmFields.nameLabel')}
              {' '}
              {!name.trim() && (
                <span className={styles.recommended}>{t('texts.kits.recommended')}</span>
              )}
            </label>
            <input
              id="kit-confirm-name"
              className={styles.input}
              value={name}
              maxLength={NAME_MAX}
              placeholder={t('texts.kits.confirmFields.namePlaceholder')}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={busy}
            />
            <div className={styles.help}>{t('texts.kits.confirmFields.nameHelp')}</div>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="kit-confirm-notes">
              {t('texts.kits.confirmFields.notesLabel')}
              {' '}
              <span className={styles.optional}>{t('texts.kits.confirmFields.notesOptional')}</span>
            </label>
            <textarea
              id="kit-confirm-notes"
              className={styles.textarea}
              value={notes}
              maxLength={NOTES_MAX}
              placeholder={t('texts.kits.confirmFields.notesPlaceholder')}
              onChange={(e) => setNotes(e.currentTarget.value)}
              disabled={busy}
            />
          </div>
        </div>

        <div className={classNames(styles.actions, className)}>
          <BadgeButton
            label={t('texts.kits.myAssemblies')}
            count={savedCount}
            onClick={openMyAssemblies}
            disabled={isOpeningAssemblies || generating}
            schema={ButtonSchema.transparent}
            size={ButtonSize.small}
            className={classNames(styles.btn, styles.outlined)}
          />
          <Button
            schema={ButtonSchema.transparent}
            size={ButtonSize.small}
            className={classNames(styles.btn, styles.outlined)}
            onClick={handleSaveAndBuildAnother}
            disabled={busy || generating}
          >
            {t('texts.kits.saveAndBuildAnother')}
          </Button>
          <Button
            schema={ButtonSchema.transparent}
            size={ButtonSize.small}
            className={classNames(styles.btn, styles.outlined)}
            onClick={handleSaveAndCopy}
            disabled={busy || generating}
          >
            {t('texts.kits.saveAndCopy')}
          </Button>
          <Button
            schema={ButtonSchema.transparent}
            size={ButtonSize.small}
            className={classNames(styles.btn, styles.outlined)}
            onClick={() => setShowPreview(true)}
            disabled={busy || generating}
          >
            {t('texts.kits.pdfPreview')}
          </Button>
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            className={classNames(styles.btn, styles.primary)}
            onClick={handleFinish}
            disabled={busy || generating}
            form={idForm}
          >
            {generating ? t('texts.kits.generating') : t('texts.kits.finish')}
          </Button>
        </div>
      </div>

      {showPopup && <MyAssembliesPopup onClose={() => setShowPopup(false)} />}
      {showPreview && <CombinedPdfPreview onClose={() => setShowPreview(false)} />}
    </>
  );
};

export default KitConfirmActions;
